import PropTypes from 'prop-types';
import React from 'react';
import { Card, CardBody } from 'reactstrap';
import './Skeleton.css';

// Loading placeholders. Keep the real layout on screen and swap only the
// content area for one of these, instead of replacing the whole page with a
// spinner. Every wrapper is aria-busy and announces "Loading" once.
//
//   Skeleton        one bar / block / circle
//   SkeletonText    n lines of text
//   SkeletonCard    card with a title and a few lines
//   SkeletonStats   a row of KPI cards
//   SkeletonChart   card with a chart-shaped block
//   SkeletonTable   table head + n rows (use `inline` inside an existing <table>)
//   SkeletonForm    label + input pairs, for edit/detail forms

const px = value => (typeof value === 'number' ? `${value}px` : value);

export const Skeleton = ({
  width = '100%',
  height,
  circle = false,
  block = false,
  className = '',
  style,
}) => (
  <span
    className={[
      'hy-skel',
      circle && 'hy-skel--circle',
      block && 'hy-skel--block',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    style={{ width: px(width), height: px(height), ...style }}
    aria-hidden='true'
  />
);

Skeleton.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  circle: PropTypes.bool,
  block: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

const Busy = ({ label = 'Loading', className = '', children }) => (
  <div className={className} role='status' aria-busy='true'>
    <span className='visually-hidden'>{label}</span>
    {children}
  </div>
);

Busy.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export const SkeletonText = ({ lines = 3, className = '' }) => (
  <Busy className={className}>
    {Array.from({ length: lines }).map((_, index) => (
      <Skeleton
        key={index}
        className={index > 0 ? 'mt-2' : ''}
        width={index === lines - 1 && lines > 1 ? '60%' : '100%'}
      />
    ))}
  </Busy>
);

SkeletonText.propTypes = {
  lines: PropTypes.number,
  className: PropTypes.string,
};

export const SkeletonCard = ({ lines = 3, className = '' }) => (
  <Card className={`border-0 shadow-sm ${className}`}>
    <CardBody>
      <Busy>
        <Skeleton width='40%' height={18} />
        <div className='mt-3'>
          {Array.from({ length: lines }).map((_, index) => (
            <Skeleton
              key={index}
              className={index > 0 ? 'mt-2' : ''}
              width={index === lines - 1 ? '70%' : '100%'}
            />
          ))}
        </div>
      </Busy>
    </CardBody>
  </Card>
);

SkeletonCard.propTypes = {
  lines: PropTypes.number,
  className: PropTypes.string,
};

export const SkeletonStats = ({ count = 4, className = '' }) => (
  <Busy className={`row g-3 ${className}`}>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='col-6 col-xl'>
        <div className='d-flex align-items-center gap-3 p-3 border rounded-3'>
          <Skeleton circle width={40} height={40} />
          <div className='flex-grow-1'>
            <Skeleton width='50%' height={20} />
            <Skeleton className='mt-2' width='80%' height={12} />
          </div>
        </div>
      </div>
    ))}
  </Busy>
);

SkeletonStats.propTypes = {
  count: PropTypes.number,
  className: PropTypes.string,
};

export const SkeletonChart = ({ height = 280, className = '' }) => (
  <Card className={`border-0 shadow-sm h-100 ${className}`}>
    <CardBody>
      <Busy>
        <Skeleton width='35%' height={18} />
        <Skeleton className='mt-3' block height={height} />
      </Busy>
    </CardBody>
  </Card>
);

SkeletonChart.propTypes = {
  height: PropTypes.number,
  className: PropTypes.string,
};

// With `inline` it renders only <tr> rows so it can sit inside an existing
// <tbody> and keep the real header; otherwise it renders a full table.
export const SkeletonTable = ({
  rows = 5,
  columns = 5,
  inline = false,
  className = '',
}) => {
  const body = Array.from({ length: rows }).map((_, row) => (
    <tr key={row}>
      {Array.from({ length: columns }).map((__, col) => (
        <td key={col}>
          <Skeleton
            width={col === 0 ? '40%' : `${60 + ((row + col) % 3) * 15}%`}
          />
        </td>
      ))}
    </tr>
  ));

  if (inline) {
    return (
      <>
        <tr className='visually-hidden'>
          <td colSpan={columns} role='status' aria-busy='true'>
            Loading
          </td>
        </tr>
        {body}
      </>
    );
  }

  return (
    <Busy className={className}>
      <table className='table hy-skel-table mb-0'>
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, col) => (
              <th key={col}>
                <Skeleton width='55%' height={12} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </Busy>
  );
};

SkeletonTable.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
  inline: PropTypes.bool,
  className: PropTypes.string,
};

export const SkeletonForm = ({ fields = 4, className = '' }) => (
  <Busy className={className}>
    {Array.from({ length: fields }).map((_, index) => (
      <div key={index} className='mb-3'>
        <Skeleton width='25%' height={12} />
        <Skeleton className='mt-2' height={36} block />
      </div>
    ))}
  </Busy>
);

SkeletonForm.propTypes = {
  fields: PropTypes.number,
  className: PropTypes.string,
};

export default Skeleton;
